import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Input, Modal, Popconfirm, Select, Skeleton, Space, Switch, Table, Tabs, Tag, Typography, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import {
  createContentTypeField,
  deleteContentTypeField,
  listContentTypeFields,
  listContentTypes,
  updateContentTypeField,
} from '../../api'
import type { SaasApiError } from '../../api'
import type { ContentTypeOut, FieldDefinitionOut, FieldOption, FieldType } from '../../types'
import '../../ui/tenant-items.css'

const { Title, Paragraph } = Typography

const FIELD_TYPE_OPTIONS: Array<{ value: FieldType; label: string }> = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'rich_text', label: 'Rich Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select (single)' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'url', label: 'URL' },
  { value: 'asset', label: 'Asset (image/file)' },
  { value: 'entry_reference', label: 'Entry reference' },
]

const OPTION_FIELD_TYPES: FieldType[] = ['select', 'multi_select']

interface FieldFormValues {
  key: string
  label: string
  field_type: FieldType
  required: boolean
  help_text: string
  optionsText?: string
  reference_content_type?: string
  public_visibility: boolean
  editor_visibility: boolean
}

function parseOptionsText(text: string | undefined): FieldOption[] {
  if (!text) return []
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, idx) => {
      const [value, label] = line.split('|').map((s) => s.trim())
      return { value: value || line, label: label || value || line, sort_order: idx }
    })
}

function optionsToText(options: FieldOption[]): string {
  return options.map((o) => (o.label && o.label !== o.value ? `${o.value}|${o.label}` : o.value)).join('\n')
}

/**
 * "Content Models" — the Fields Manager admin surface (Generic Content
 * Schema Engine, Phase A2 spec §20/§22). A tenant admin lists/adds/edits/
 * archives FieldDefinition rows for one ContentType here; the Dynamic
 * Item Editor (EntryManagerTab) and the Generic Public Renderer both
 * pick up whatever is saved here automatically — no React/backend source
 * change required for a new field (spec's core acceptance sentence).
 */
export default function ContentModelsTab({ workspaceId, siteId }: { workspaceId: string; siteId: string }) {
  const [contentTypes, setContentTypes] = useState<ContentTypeOut[] | null>(null)
  const [activeKey, setActiveKey] = useState<string>('')
  const [fields, setFields] = useState<FieldDefinitionOut[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<FieldDefinitionOut | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm<FieldFormValues>()
  const fieldType = Form.useWatch('field_type', form)

  const loadContentTypes = useCallback(() => {
    setError(null)
    listContentTypes(workspaceId, siteId)
      .then((r) => {
        setContentTypes(r.data)
        if (r.data.length > 0) setActiveKey((prev) => prev || r.data[0].id)
      })
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId])

  useEffect(loadContentTypes, [loadContentTypes])

  const loadFields = useCallback(() => {
    if (!activeKey) return
    listContentTypeFields(workspaceId, siteId, activeKey)
      .then((r) => setFields(r.data))
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId, activeKey])

  useEffect(() => {
    setFields(null)
    loadFields()
  }, [loadFields])

  const activeContentType = contentTypes?.find((ct) => ct.id === activeKey) ?? null

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({ field_type: 'text', required: false, public_visibility: true, editor_visibility: true })
    setModalOpen(true)
  }

  const openEdit = (field: FieldDefinitionOut) => {
    setEditing(field)
    form.setFieldsValue({
      key: field.key,
      label: field.label,
      field_type: field.field_type,
      required: field.required,
      help_text: field.help_text,
      optionsText: optionsToText(field.options),
      reference_content_type: (field.config?.content_type as string | undefined) ?? undefined,
      public_visibility: field.public_visibility,
      editor_visibility: field.editor_visibility,
    })
    setModalOpen(true)
  }

  const onSubmit = async () => {
    const values = await form.validateFields()
    setSubmitting(true)
    try {
      const options = OPTION_FIELD_TYPES.includes(values.field_type) ? parseOptionsText(values.optionsText) : []
      const config = values.field_type === 'entry_reference' && values.reference_content_type ? { content_type: values.reference_content_type } : {}
      if (editing) {
        await updateContentTypeField(workspaceId, siteId, editing.id, {
          label: values.label,
          required: values.required,
          help_text: values.help_text,
          options,
          config,
          public_visibility: values.public_visibility,
          editor_visibility: values.editor_visibility,
        })
        message.success(`Field "${values.label}" updated`)
      } else {
        await createContentTypeField(workspaceId, siteId, activeKey, {
          key: values.key,
          label: values.label,
          field_type: values.field_type,
          required: values.required,
          help_text: values.help_text,
          options,
          config,
          public_visibility: values.public_visibility,
          editor_visibility: values.editor_visibility,
          sort_order: fields?.length ?? 0,
        })
        message.success(`Field "${values.label}" created`)
      }
      setModalOpen(false)
      loadFields()
    } catch (err) {
      message.error((err as SaasApiError).message ?? 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onArchiveToggle = async (field: FieldDefinitionOut) => {
    try {
      await updateContentTypeField(workspaceId, siteId, field.id, { status: field.status === 'active' ? 'archived' : 'active' })
      message.success(field.status === 'active' ? `"${field.label}" archived` : `"${field.label}" reactivated`)
      loadFields()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  const onDelete = async (field: FieldDefinitionOut) => {
    try {
      await deleteContentTypeField(workspaceId, siteId, field.id)
      message.success(`Field "${field.label}" deleted`)
      loadFields()
    } catch (err) {
      const apiErr = err as SaasApiError
      message.error(apiErr.message || 'Delete failed — try archiving instead')
    }
  }

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  if (contentTypes === null) return <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 16 }} />

  return (
    <div style={{ marginTop: 16 }}>
      <Paragraph type="secondary">
        Define custom fields for each content type. New fields appear automatically in the editor and on the public
        website once published — no code change required.
      </Paragraph>
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        items={contentTypes.map((ct) => ({ key: ct.id, label: ct.plural_label || ct.label || ct.key }))}
      />
      {activeContentType && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Title level={5} style={{ margin: 0 }}>
              {activeContentType.label || activeContentType.key} fields
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add Field
            </Button>
          </div>
          {fields === null ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : (
            <Table
              className="tc-data-table"
              rowKey="id"
              dataSource={fields}
              pagination={false}
              sticky={{ offsetHeader: 64 }}
              columns={[
                { title: 'Key', dataIndex: 'key', align: 'left' as const, render: (v: string) => <code>{v}</code> },
                { title: 'Label', dataIndex: 'label', align: 'left' as const },
                { title: 'Type', dataIndex: 'field_type' },
                { title: 'Required', dataIndex: 'required', render: (v: boolean) => (v ? <Tag color="orange">Required</Tag> : null) },
                {
                  title: 'Visibility',
                  render: (_: unknown, f: FieldDefinitionOut) => (
                    <Space size={4} style={{ display: 'flex', justifyContent: 'center' }}>
                      {f.public_visibility && <Tag color="blue">Public</Tag>}
                      {!f.editor_visibility && <Tag>Hidden in editor</Tag>}
                    </Space>
                  ),
                },
                { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag> },
                {
                  title: 'Actions',
                  render: (_: unknown, f: FieldDefinitionOut) => (
                    <Space style={{ display: 'flex', justifyContent: 'center' }}>
                      <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(f)} />
                      <Button size="small" onClick={() => onArchiveToggle(f)}>
                        {f.status === 'active' ? 'Archive' : 'Reactivate'}
                      </Button>
                      <Popconfirm title={`Delete field "${f.label}"?`} onConfirm={() => onDelete(f)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </div>
      )}

      <Modal
        title={editing ? `Edit field "${editing.label}"` : 'Add Field'}
        open={modalOpen}
        onOk={onSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editing ? 'Save' : 'Create'}
        confirmLoading={submitting}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="key"
            label="Key"
            tooltip="Stable machine identity — cannot be changed after creation. Use lowercase letters/numbers/underscores."
            rules={[{ required: true, message: 'Key is required' }, { pattern: /^[a-z][a-z0-9_]*$/, message: 'lowercase letters, numbers, underscores' }]}
          >
            <Input disabled={!!editing} placeholder="e.g. condition" />
          </Form.Item>
          <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input placeholder="e.g. Condition" />
          </Form.Item>
          <Form.Item name="field_type" label="Field Type" rules={[{ required: true }]}>
            <Select options={FIELD_TYPE_OPTIONS} disabled={!!editing} />
          </Form.Item>
          {OPTION_FIELD_TYPES.includes(fieldType) && (
            <Form.Item
              name="optionsText"
              label="Options"
              tooltip="One option per line. Use value|Label to set a display label, or just the value."
              rules={[{ required: true, message: 'At least one option is required' }]}
            >
              <Input.TextArea rows={4} placeholder={'excellent|Excellent\ngood|Good\nfair|Fair'} />
            </Form.Item>
          )}
          {fieldType === 'entry_reference' && (
            <Form.Item name="reference_content_type" label="Reference content type" tooltip="Restrict which content type this field may reference (e.g. exhibition)">
              <Input placeholder="e.g. exhibition" />
            </Form.Item>
          )}
          <Form.Item name="help_text" label="Help text">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="required" label="Required" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="public_visibility" label="Show on public website" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="editor_visibility" label="Show in editor" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
